// Tela de Clientes (issue #5, redesenhada): filtros + tabela + modal de
// cadastro/edição com a grade de Paradas (coleta e devolução) embutida.

import { descreveCiclo } from "../../domain/clientes.ts";
import { emTitulo } from "../../lib/em-titulo.ts";
import { paradasDoCliente } from "../../domain/paradas.ts";
import { newId } from "../../domain/ids.ts";
import type {
  BaseFaturamento,
  CicloFechamento,
  Cliente,
} from "../../domain/snapshot.ts";
import type { Store } from "../store.ts";
import { clear, el, openModal, tabelaPaginada, botao } from "../ui.ts";
import { criarAcaoComando } from "./_acao.ts";
import {
  barraCadastro,
  campo,
  campoObrigatorio,
  inputTexto,
  telaLista,
} from "./_cadastro.ts";
import { listaSelect } from "./_lista-select.ts";
import { paradasEditor } from "./_paradas-editor.ts";
import { tiposVisiveisEditor } from "./_tipos-visiveis.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo, SITUACAO_CADASTRO } from "./_lista-filtros.ts";
import { podeEditarModulo } from "./_permissao.ts";

export function renderClientes(
  container: HTMLElement,
  store: Store,
): () => void {
  // filtros, busca e página num estado só (#93): null = todos; ordena por Cliente
  const lista = new EstadoLista(
    {
      tipo: null as string[] | null,
      faturamento: null as string[] | null,
      ciclo: null as string[] | null,
      situacao: null as string[] | null,
    },
    { ordCol: 0 },
  );
  const busca = buscaDaLista(lista, "Buscar por nome, código ou tipo…", () => render());
  const fTipo = filtroMultiplo(lista, "tipo", "Tipo", () => render());
  const fFaturamento = filtroMultiplo(lista, "faturamento", "Faturamento", () => render());
  const fCiclo = filtroMultiplo(lista, "ciclo", "Ciclo", () => render());
  const fSituacao = filtroMultiplo(lista, "situacao", "Situação", () => render());

  const abrirModal = new URLSearchParams(
    location.hash.split("?")[1] ?? "",
  ).get("edit");

  const render = (): void => {
    clear(container);
    const clientes = store.get().clientes;
    const podeGravar = podeEditarModulo(store, "configuracao");

    const tiposPresentes = [
      ...new Set(clientes.map((c) => c.tipo).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b, "pt-BR"));

    fTipo.atualizar(tiposPresentes.map((t) => ({ id: t, nome: t })));
    fFaturamento.atualizar([
      { id: "entrada", nome: "Entrada" },
      { id: "saida", nome: "Saída" },
    ]);
    fCiclo.atualizar([
      { id: "mensal", nome: "Mensal" },
      { id: "quinzenal", nome: "Quinzenal" },
      { id: "customizado", nome: "Customizado" },
    ]);
    fSituacao.atualizar(SITUACAO_CADASTRO);
    const tipoFiltro = fTipo.selecionados();
    const fatFiltro = fFaturamento.selecionados();
    const cicloFiltro = fCiclo.selecionados();
    const sitFiltro = fSituacao.selecionados();
    const filtrados = clientes
      .filter((c) => tipoFiltro.size === 0 || tipoFiltro.has(c.tipo))
      .filter((c) => fatFiltro.size === 0 || fatFiltro.has(c.baseFaturamento))
      .filter((c) => cicloFiltro.size === 0 || cicloFiltro.has(c.cicloFechamento.tipo))
      .filter((c) =>
        sitFiltro.size === 0 || sitFiltro.has(c.ativo ? "ativo" : "inativo"),
      )
      .filter((c) => busca.casa(c.nome, c.codigo, c.tipo))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    const barra = barraCadastro(
      [fTipo.element, fFaturamento.element, fCiclo.element, fSituacao.element],
      "Novo Cliente",
      () => abrirModalCliente(null),
      () => {
        lista.limpar();
        render();
      },
      undefined,
      podeGravar,
    );

    // --- tabela ---
    if (filtrados.length === 0) {
      container.append(
        telaLista(
          barra,
          busca.elemento(),
          el("p", { class: "muted lista-vazia" }, lista.mensagemVazio({ nenhum: "Nenhum cliente.", filtrado: "Nenhum cliente com esses filtros." })),
        ),
      );
      return;
    }
    const linhas = filtrados.map((c) => {
      const acoes: Node[] = [];
      if (podeGravar) {
        const editar = botao("secundario", {}, "Editar");
        editar.addEventListener("click", () => abrirModalCliente(c));
        const alternar = botao(
          "secundario",
          {},
          c.ativo ? "Inativar" : "Reativar",
        );
        alternar.addEventListener("click", () => {
          void store.executar(
            {
              agregado: "cliente",
              cmd: { tipo: "definirAtivo", id: c.id, ativo: !c.ativo },
            },
            c.ativo ? "Cliente inativado." : "Cliente reativado.",
          );
        });
        acoes.push(editar, alternar);
      }
      const nParadas = paradasDoCliente(store.get(), c.id).length;
      return el(
        "tr",
        { class: c.ativo ? "" : "inativo" },
        el("td", {}, c.nome),
        el("td", { class: "muted" }, c.codigo),
        el("td", { class: "muted" }, c.tipo || "—"),
        el(
          "td",
          { class: "muted" },
          c.baseFaturamento === "entrada" ? "Entrada" : "Saída",
        ),
        el("td", { class: "muted" }, emTitulo(descreveCiclo(c.cicloFechamento))),
        el("td", { class: "muted" }, nParadas === 0 ? "—" : `${nParadas}`),
        el(
          "td",
          {},
          c.ativo
            ? el("span", { class: "estado-ativo" }, "Ativo")
            : el("span", { class: "estado-inativo" }, "Inativo"),
        ),
        el("td", { class: "acoes" }, ...acoes),
      );
    });
    container.append(
      telaLista(
        barra,
        busca.elemento(),
        tabelaPaginada(
          "clientes",
          ["Cliente", "Código", "Tipo", "Faturamento", "Ciclo", "Paradas", "Situação", ""],
          linhas,
          lista.tabela,
          render,
        ),
      ),
    );
  };

  const abrirModalCliente = (atual: Cliente | null): void => {
    openModal(
      atual ? `Editar ${atual.nome}` : "Novo Cliente",
      (fechar) => {
        const erro = el("p", { class: "form-erro", role: "alert" });
        const nome = inputTexto("Nome do hotel", atual?.nome ?? "");
        const codigo = inputTexto("AAA", atual?.codigo ?? "", {
          maxlength: "3",
          style: "text-transform:uppercase",
        });
        const endereco = inputTexto("Endereço", atual?.endereco ?? "");
        const contato = inputTexto("Contato principal", atual?.contato ?? "");
        const tipo = listaSelect(
          store,
          "tiposCliente",
          atual?.tipo ?? "",
        );

        const base = el("select", {}) as HTMLSelectElement;
        for (const [v, rotulo] of [
          ["saida", "Saída — fatura o que devolvemos"],
          ["entrada", "Entrada — fatura o que o hotel declarou"],
        ] as const) {
          const o = el("option", { value: v }, rotulo) as HTMLOptionElement;
          if ((atual?.baseFaturamento ?? "saida") === v) o.selected = true;
          base.append(o);
        }

        const paradas = paradasEditor(
          atual ? paradasDoCliente(store.get(), atual.id) : [],
          atual?.devolucaoNoMesmoDia ?? false,
        );
        const tiposVisiveis = tiposVisiveisEditor(
          store.get(),
          atual?.tiposPecaVisiveis ?? null,
        );

        const ciclo = el("select", {}) as HTMLSelectElement;
        for (const [v, rotulo] of [
          ["mensal", "Mensal"],
          ["quinzenal", "Quinzenal"],
          ["customizado", "Customizado (dia inicial e final)"],
        ] as const) {
          const o = el("option", { value: v }, rotulo) as HTMLOptionElement;
          if ((atual?.cicloFechamento.tipo ?? "mensal") === v) o.selected = true;
          ciclo.append(o);
        }
        const diaIni = diaInput(
          atual?.cicloFechamento.tipo === "customizado"
            ? atual.cicloFechamento.diaInicio
            : 1,
        );
        const diaFim = diaInput(
          atual?.cicloFechamento.tipo === "customizado"
            ? atual.cicloFechamento.diaFim
            : 30,
        );
        const dias = el("div", { class: "sufixo" }, "do dia ", diaIni, " ao ", diaFim);
        const sincDias = (): void => {
          dias.hidden = ciclo.value !== "customizado";
        };

        // --- Dia de Vencimento (#40) ---
        // Um campo nos Ciclos mensal e customizado; dois no quinzenal, um por
        // quinzena. Em branco = não configurado, e a Fatura abre o vencimento
        // vazio para o operador digitar.
        const venc = (valor: number | undefined): HTMLInputElement => {
          const i = el("input", {
            type: "number",
            min: "1",
            max: "31",
            placeholder: "—",
            class: "dia-venc",
          }) as HTMLInputElement;
          if (valor != null) i.value = String(valor);
          return i;
        };
        const venc1 = venc(atual?.diasVencimento[0]);
        const venc2 = venc(atual?.diasVencimento[1]);
        const rotulo1 = el("span", {}, "Vence dia ");
        const rotulo2 = el("span", {}, " · 2ª quinzena, dia ");
        const linhaVenc = el(
          "label",
          { class: "campo-venc" },
          el("span", { class: "campo-rotulo" }, "Dia de Vencimento"),
          el("span", { class: "sufixo" }, rotulo1, venc1, rotulo2, venc2),
        );
        const sincVenc = (): void => {
          const quinzenal = ciclo.value === "quinzenal";
          rotulo1.textContent = quinzenal ? "1ª quinzena, dia " : "Vence dia ";
          rotulo2.hidden = !quinzenal;
          venc2.hidden = !quinzenal;
        };

        const sincCiclo = (): void => {
          sincDias();
          sincVenc();
        };
        ciclo.addEventListener("change", sincCiclo);
        sincCiclo();

        const salvar = (): void => {
          erro.textContent = "";
          const cicloValor: CicloFechamento =
            ciclo.value === "customizado"
              ? {
                  tipo: "customizado",
                  diaInicio: Number(diaIni.value),
                  diaFim: Number(diaFim.value),
                }
              : { tipo: ciclo.value === "quinzenal" ? "quinzenal" : "mensal" };
          const dados = {
            nome: nome.value,
            endereco: endereco.value,
            contato: contato.value,
            codigo: codigo.value,
            tipo: tipo.value(),
            baseFaturamento: base.value as BaseFaturamento,
            cicloFechamento: cicloValor,
            devolucaoNoMesmoDia: paradas.devolucaoNoMesmoDia(),
            // manda só os campos que o Ciclo comporta; em branco vira lista
            // vazia = "não configurado" (o domínio recusa meia configuração)
            diasVencimento: (cicloValor.tipo === "quinzenal"
              ? [venc1.value, venc2.value]
              : [venc1.value]
            )
              .filter((v) => v.trim() !== "")
              .map(Number),
            paradas: paradas.paradas(),
            tiposPecaVisiveis: tiposVisiveis.valor(),
          };
          const id = atual?.id ?? newId();
          criarAcaoComando(store, erro)(
            {
              agregado: "cliente",
              cmd: {
                tipo: atual ? "editar" : "criar",
                id,
                dados,
              },
            },
            undefined,
            fechar,
          );
        };

        const cancelar = botao("secundario", {}, "Cancelar");
        cancelar.addEventListener("click", fechar);
        const ok = botao("primario", { type: "submit" }, atual ? "Salvar" : "Criar Cliente");

        const form = el(
          "form",
          { class: "form-modal" },
          campoObrigatorio("Nome", nome),
          campo("Código (3 letras)", codigo),
          campo("Endereço", endereco),
          campo("Contato", contato),
          campo("Tipo", tipo.element),
          campo("Base de faturamento", base),
          paradas.element,
          campo("Ciclo de fechamento", ciclo, dias),
          linhaVenc,
          tiposVisiveis.element,
          erro,
          el("div", { class: "modal-actions" }, cancelar, ok),
        );
        form.addEventListener("submit", (ev) => {
          ev.preventDefault();
          salvar();
        });
        return form;
      },
      "largo",
    );
  };

  render();

  if (abrirModal && podeEditarModulo(store, "configuracao")) {
    const c = store
      .get()
      .clientes.find((x) => x.codigo.toLowerCase() === abrirModal.toLowerCase());
    if (c) abrirModalCliente(c);
  }

  return store.subscribe(render);
}

function diaInput(value: number): HTMLInputElement {
  const i = el("input", { type: "number", min: "1", max: "31" }) as HTMLInputElement;
  i.value = String(value);
  return i;
}
